import React, { FC, Dispatch } from 'react'
import { Button, Header } from '@lightspeed/design-system-react'
import { VendImage } from '@vend/react-utilities'
import {
  PAYMENT_STATUS,
  TERMINAL_EVENT,
  TerminalEventMonitoring,
} from '@lightspeed-payments/payments-terminal-sdk'
import { trackUserEvent } from '@vend/utilities'
import { useT } from '@transifex/react'
import { PaymentEvent } from '../hooks/use-lspay-payment-server-reducer/types'
import { LSPAYModalContent } from '../../../modal-states/LSPAYModalContent'

import { usePollTransaction } from '../hooks/use-poll-transaction/usePollTransaction'
import {
  cancelTransaction,
  isTransactionCompleted,
} from '../../../../helpers/lspayTransaction'
import {
  LSPAY_ERROR,
  LSPAY_TRANSACTION_STATUS,
  TRANSACTION_TYPE,
} from '../../../../helpers/lspayConstants'
import { WEB_REGISTER } from '../../../../../../web-register.constant'
import { TAG, trackLSPayServerError } from '../../../../hooks/errorTracking'
import { TerminalInfo } from '../../../../helpers/lspay.types'
import { getTotalSurchargeAmountFromTenders } from '../../../../../../../functions/payments'
import { useErrorMessages } from '../../../../hooks/useErrorMessages'

interface PollTransactionProps {
  paymentIntentId: string
  registerId: string
  registerSalePaymentId: string
  registerSaleId: string
  dispatch: Dispatch<PaymentEvent>
  terminalMonitoringInstance: TerminalEventMonitoring | null
  terminalInfo: TerminalInfo
}

export const PollTransaction: FC<PollTransactionProps> = ({
  paymentIntentId,
  registerId,
  registerSalePaymentId,
  registerSaleId,
  dispatch,
  terminalMonitoringInstance,
  terminalInfo,
}) => {
  const [isCancelling, setIsCancelling] = React.useState(false)
  const getErrorMessage = useErrorMessages()
  const cancelPolling = usePollTransaction({
    paymentIntentId,
    registerId,
    registerSalePaymentId,
    registerSaleId,
    terminalMonitoringInstance,
    dispatch,
    terminalInfo,
  })
  const { deviceId, deviceModel } = terminalInfo
  const t = useT()

  const handleCancelPayment = async () => {
    // Track the event for clicking on the cancel payment button
    trackUserEvent(
      WEB_REGISTER.DISPLAY_NAME,
      'LSD clicked cancel payment button',
      {
        registerSalePaymentId,
        registerSaleId,
        paymentIntentId,
      }
    )
    try {
      setIsCancelling(true)
      const response = await cancelTransaction({
        registerId,
        registerSalePaymentId,
        paymentIntentId,
      })
      // cancel polling after getting the response from cancelTransaction
      cancelPolling()
      if (response.status === LSPAY_TRANSACTION_STATUS.CANCELLED) {
        terminalMonitoringInstance?.sendEvent(TERMINAL_EVENT.COMPLETE, {
          payment: {
            id: paymentIntentId,
            status: PAYMENT_STATUS.CANCELLED,
            reason: 'Requested by customer',
          },
          terminal: {
            id: deviceId,
            model: deviceModel,
          },
          transactionType: TRANSACTION_TYPE.PAYMENT,
        })

        dispatch({
          type: 'CANCELLED',
          errorMessage: getErrorMessage(LSPAY_ERROR.CANCEL_PAYMENT),
          paymentIntentId: response.payment_intent_id,
          reason: response.reason,
        })
      } else if (isTransactionCompleted(response)) {
        terminalMonitoringInstance?.sendEvent(TERMINAL_EVENT.COMPLETE, {
          payment: {
            id: paymentIntentId,
            status: PAYMENT_STATUS.APPROVED,
          },
          terminal: {
            id: deviceId,
            model: deviceModel,
          },
          transactionType: TRANSACTION_TYPE.PAYMENT,
          ...(getTotalSurchargeAmountFromTenders(response.transaction.tenders)
            ? { surcharging: true }
            : {}),
        })
        dispatch({
          type: 'POLL_RESULT_OK',
          transaction: response.transaction,
        })
      } else if (response.status === LSPAY_TRANSACTION_STATUS.AUTHORISED) {
        dispatch({
          type: 'AUTHORISED',
          paymentIntentId: response.payment_intent_id,
        })
        return
      }
    } catch (error: any) {
      // track the error
      trackLSPayServerError(
        '[lspay-sever-driven][recovery] failed to cancel transaction',
        error,
        {
          registerSalePaymentId,
          paymentIntentId,
        },
        TAG.payment
      )
      // absorb this error, there's nothing to do about it
    } finally {
      setIsCancelling(false)
    }
  }

  return (
    <LSPAYModalContent
      actions={
        <Button
          variant="no"
          className="vd-mr2"
          loading={isCancelling}
          onClick={handleCancelPayment}
          data-track="cancel-payment-poll-transaction"
        >
          {t('Cancel Payment', {
            _context: 'Cancel payment button',
          })}
        </Button>
      }
    >
      <VendImage
        relativeImagePath="payments/insert-card-v1.svg"
        relativeDarkImagePath="payments/insert-card-darkmode-v1.svg"
        className="vd-mb2"
      />
      <Header type="sub-heading" level="2">
        {t('Tap, insert or swipe card.', {
          _context: 'Awaiting for card modal header',
        })}
      </Header>
    </LSPAYModalContent>
  )
}
